import React, { useContext, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormContext } from '../../App';
import BuilderHeader from './BuilderHeader';
import ResizablePanel from './ResizablePanel';
import FloatingToolbar from './FloatingToolbar';
import TreeNavigation from '../builder/TreeNavigation';
import FormCanvas from '../builder/FormCanvas';
import PropertyPanel from '../builder/PropertyPanel/PropertyPanel';
import SectionModal from '../builder/SectionModal';
import QuestionTypePickerModal from '../builder/QuestionTypePickerModal';
import MoveFieldModal from '../builder/MoveFieldModal';
import MoveSectionModal from '../builder/MoveSectionModal';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import ConfirmDialog from '../../core/utilities/ConfirmPopup';
import UnsavedFieldChangesModal from '../UnsavedFieldChangesModal/UnsavedFieldChangesModal';
import DeleteSectionModal from '../../pages/DeleteSectionModal/DeleteSectionModal';
import FormCreate from '../../pages/FormCreate/FormCreate';
import ShareFormModal from '../../pages/ShareFormModal/ShareFormModal';
import { usePanelResize } from '../../hooks/usePanelResize';
import { useBuilderSelection } from '../../hooks/useBuilderSelection';
import { useUndoRedo } from '../../hooks/useUndoRedo';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useBuilderEditor } from '../../features/builder/BuilderEditorContext';
import './builder-shell.scss';

const BuilderShellInner = ({ userFullName }) => {
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const { widths, setSidebarWidth, setPanelWidth } = usePanelResize();
  const {
    selectedSectionId, selectedFieldId, propertyPanelOpen, setPropertyPanelOpen,
    selectField, selectSection, clearSelection,
  } = useBuilderSelection();
  const { canUndo, canRedo, undo, redo, pushCommand } = useUndoRedo();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [showShare, setShowShare] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const { formObj } = useContext(FormContext);
  const editPermissions = formObj?.Permissions;

  const editor = useBuilderEditor();
  const {
    formId, langId, formdata, getSectionDetails, submitlanguage, setIsSubsection,
    getFieldWithoutSection, startNewQuestion, openSectionModal, confirmDeleteField,
    confirmDeleteSection, isConfirmModal, confirmMessage, isDeleteSection,
    deleteSectionId, updateform, setUpdateForm, callbackFn, handleSaveField,
    duplicateField, deleteField, resetFieldProperties, editField, lastCreatedItem, lastMovedItem,
    publishForm, guardAction, showUnsavedChangesModal, confirmSaveAndContinue,
    confirmDiscardAndContinue, cancelUnsavedChangesModal,
  } = editor;

  useEffect(() => {
    if (!lastCreatedItem) return;
    if (lastCreatedItem.type === 'section') {
      selectSection(lastCreatedItem.id);
    } else if (lastCreatedItem.type === 'field') {
      selectField(lastCreatedItem.id, lastCreatedItem.sectionId, { openPanel: false });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastCreatedItem]);

  // A drag-and-drop move just relocated this item — make it the active/selected
  // item, same as a freshly-created item is selected above.
  useEffect(() => {
    if (!lastMovedItem) return;
    if (lastMovedItem.type === 'section') {
      selectSection(lastMovedItem.id);
    } else if (lastMovedItem.type === 'field') {
      selectField(lastMovedItem.id, lastMovedItem.sectionId, { openPanel: false });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMovedItem]);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useKeyboardShortcuts({
    onSearch: () => searchRef.current?.focus(),
    onUndo: undo,
    onRedo: redo,
    onSave: () => handleSaveField(!!selectedFieldId),
    onDuplicate: () => {
      const field = editor.formsectionsdata?.fields?.find((f) => f.id === selectedFieldId);
      if (field) duplicateField(field);
    },
    onDelete: () => {
      const field = editor.formsectionsdata?.fields?.find((f) => f.id === selectedFieldId)
        || editor.formdata?.fields?.find((f) => f.id === selectedFieldId)
        || (() => {
          const walk = (sections) => {
            for (const s of sections || []) {
              const found = s.fields?.find((f) => f.id === selectedFieldId);
              if (found) return found;
              const sub = walk(s.subSections);
              if (sub) return sub;
            }
            return null;
          };
          return walk(editor.formdata?.sections);
        })();
      if (!field) return;
      const sectionId = editor.findSectionIdForField?.(field.id) ?? selectedSectionId ?? 0;
      deleteField(field, sectionId);
    },
    onEscape: () => guardAction(() => { clearSelection(); resetFieldProperties(); setPropertyPanelOpen(false); }),
  });

  const handleChooseQuestionType = () => {
    clearSelection();
    setPropertyPanelOpen(true);
  };

  const goPreview = () => {
    guardAction(() => {
      let url = `/form_builder/form_preview/${formId}`;
      if (langId) url += `/${langId}`;
      navigate(url);
    });
  };

  return (
    <div
      className="builder-shell"
      style={{
        '--builder-sidebar-width': (sidebarOpen && !isMobile) ? `${widths.sidebar}px` : '0px',
        '--builder-panel-width': (propertyPanelOpen && !isMobile) ? `${widths.panel}px` : '0px',
      }}
    >
      <BuilderHeader
        userFullName={userFullName}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        onShare={() => setShowShare(true)}
        onPublish={() => guardAction(() => setConfirmPublish(true))}
        canPublish={formdata?.status !== 'PUBLISHED' && !!editPermissions}
        isPublished={formdata?.status === 'PUBLISHED'}
        searchRef={searchRef}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen((v) => !v)}
      />

      <div className="builder-shell__body">
        {isMobile && sidebarOpen && (
          <button
            type="button"
            className="builder-sidebar-backdrop"
            aria-label="Close sidebar"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {sidebarOpen && (
          <ResizablePanel
            id="form-sidebar"
            width={widths.sidebar}
            onResize={setSidebarWidth}
            side="left"
          >
            <TreeNavigation
              selectedSectionId={selectedSectionId}
              selectedFieldId={selectedFieldId}
              lastCreatedItem={lastCreatedItem}
              lastMovedItem={lastMovedItem}
              onSelectSection={(sectionId) => {
                selectSection(sectionId);
                if (isMobile) setSidebarOpen(false);
              }}
              onSelectField={(fieldId, sectionId) => {
                const field = editor.formsectionsdata?.fields?.find((f) => f.id === fieldId)
                  || editor.formdata?.fields?.find((f) => f.id === fieldId)
                  || (() => {
                    const walk = (sections) => {
                      for (const s of sections || []) {
                        const found = s.fields?.find((f) => f.id === fieldId);
                        if (found) return found;
                        const sub = walk(s.subSections);
                        if (sub) return sub;
                      }
                      return null;
                    };
                    return walk(editor.formdata?.sections);
                  })();
                if (!field) return;
                if (sectionId && editor.currentsection !== sectionId) {
                  editor.viewSection(sectionId, fieldId);
                } else if (sectionId === 0) {
                  editor.getFieldWithoutSection();
                }
                selectField(fieldId, sectionId, { openPanel: false });
                editField(field, sectionId);
                if (isMobile) setSidebarOpen(false);
              }}
              onSelectRootFields={() => {
                getFieldWithoutSection();
                selectSection(0);
                if (isMobile) setSidebarOpen(false);
              }}
            />
          </ResizablePanel>
        )}

        <div className="builder-shell__canvas-wrap">
          <FormCanvas
            selectedFieldId={selectedFieldId}
            selectedSectionId={selectedSectionId}
            onSelectField={(field) => selectField(field.id, editor.formsectionsdata?.id)}
            isLoading={!editor.isFormLoaded}
          />
        </div>

        {propertyPanelOpen && !isMobile && (
          <ResizablePanel
            width={widths.panel}
            onResize={setPanelWidth}
            side="right"
            className="builder-resizable-panel--right"
          >
            <PropertyPanel
              isOpen={propertyPanelOpen}
              onClose={() => setPropertyPanelOpen(false)}
              isMobile={isMobile}
            />
          </ResizablePanel>
        )}
      </div>

      {propertyPanelOpen && isMobile && (
        <PropertyPanel
          isOpen={propertyPanelOpen}
          onClose={() => setPropertyPanelOpen(false)}
          isMobile={isMobile}
        />
      )}

      <FloatingToolbar
        onAddQuestion={() => startNewQuestion(!!editor.formsectionsdata?.sectionAvailable)}
        onAddSection={() => openSectionModal(1)}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        onPreview={goPreview}
      />

      <SectionModal />
      <QuestionTypePickerModal onSelect={handleChooseQuestionType} />
      <MoveFieldModal />
      <MoveSectionModal />

      {isConfirmModal && (
        <ConfirmationModal
          modalShow={isConfirmModal}
          messageText={confirmMessage}
          callbackModal={confirmDeleteField}
        />
      )}

      {isDeleteSection && (
        <DeleteSectionModal
          modalShow={isDeleteSection}
          callbackModal={confirmDeleteSection}
        />
      )}

      {updateform && (
        <FormCreate isRename={updateform} form={formdata} callbackFn={callbackFn} />
      )}

      {showShare && formdata && (
        <ShareFormModal
          modalShow={showShare}
          data={formdata}
          callbackModal={() => setShowShare(false)}
        />
      )}

      <ConfirmDialog
        open={confirmPublish}
        setOpen={setConfirmPublish}
        onConfirm={publishForm}
      >
        <div className="text-center">Are you sure want to publish?</div>
      </ConfirmDialog>

      <UnsavedFieldChangesModal
        show={showUnsavedChangesModal}
        onSaveAndContinue={confirmSaveAndContinue}
        onDiscardChanges={confirmDiscardAndContinue}
        onCancel={cancelUnsavedChangesModal}
      />
    </div>
  );
};

export default BuilderShellInner;
